import { useState } from 'react';
import { SOCIAL_LINK_OPTIONS, type SocialLink } from 'utils/shared/playerFinder';
import FormTextInput from 'components/Find/FormTextInput';
import Switch from 'components/forms/Switch';

type SocialLinkValues = Record<SocialLink, string>;

interface Props {
  value: SocialLinkValues;
  onChange: (value: SocialLinkValues) => void;
}

/**
 * A row per network, each a toggle that reveals its input. A row is "on" once it
 * holds a value; toggling off clears it, so the toggle needs its own memory of
 * rows the player opened but hasn't filled.
 */
export default function SocialLinksField({ value, onChange }: Props) {
  const [openRows, setOpenRows] = useState<SocialLink[]>(() =>
    SOCIAL_LINK_OPTIONS.filter((option) => !!value[option.value]).map((option) => option.value),
  );

  const toggleRow = (key: SocialLink) => {
    if (openRows.includes(key)) {
      setOpenRows(openRows.filter((entry) => entry !== key));
      onChange({ ...value, [key]: '' });
      return;
    }
    setOpenRows([...openRows, key]);
  };

  return (
    <div className="flex flex-col gap-ds-lg">
      {SOCIAL_LINK_OPTIONS.map((option) => {
        const isOpen = openRows.includes(option.value);

        return (
          <div key={option.value} className="flex flex-col gap-ds-md">
            <Switch
              isActive={isOpen}
              toggleIsActive={() => toggleRow(option.value)}
              labelRight={option.label}
              labelRightClassName="typography-product-body text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
              className="!max-w-none !justify-start gap-ds-md lg:!ml-0"
            />

            {isOpen && (
              <FormTextInput
                value={value[option.value]}
                onChange={(event) => onChange({ ...value, [option.value]: event.target.value })}
                placeholder={option.placeholder}
                aria-label={`${option.label} link`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
